import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AuthLayout } from "./AuthLayout";
import { SocialButtons } from "./SocialButtons";
import { Button, Callout, TextField } from "@/design/components";
import { useAuth } from "@/app/auth";
import { ApiError } from "@/api/client";
import { resendVerification } from "@/api/auth";
import { PROVIDER_LABEL, type SocialProvider } from "@/api/oauth";
import styles from "./AuthForm.module.css";

const loginSchema = z.object({
  email: z.string().trim().email("Enter a valid email"),
  password: z.string().min(1, "Enter your password"),
});
type LoginValues = z.infer<typeof loginSchema>;

export function LoginScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = useAuth();
  const [formError, setFormError] = useState<string | null>(null);
  const noticeRef = useRef<HTMLDivElement>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginValues>({ resolver: zodResolver(loginSchema) });

  // Return to wherever a protected route bounced from, else the dashboard.
  const navState = location.state as
    | { from?: string; expired?: boolean; registered?: boolean; email?: string }
    | null;
  const from = navState?.from ?? "/app";
  // Shown only when a live session expired mid-use (RequireAuth flags it), so the
  // user understands why they're back at login rather than facing a blank prompt.
  // Snapshot at mount: we scrub `expired` from the history entry below, so derive
  // it once here rather than from (about-to-change) nav state.
  const [sessionExpired] = useState(() => navState?.expired === true);
  // Just registered (email+password) → the account exists but is UNVERIFIED; the API
  // blocks login until the emailed link is clicked. Surface that here BEFORE they try,
  // so they don't hit a bare "Email not verified" error. (Social sign-up auto-verifies,
  // so it never sets this flag.)
  const [registered] = useState(() => navState?.registered === true);
  const [registrationEmail] = useState(() =>
    typeof navState?.email === "string" ? navState.email : null,
  );
  const [resent, setResent] = useState(false);
  // A failed social-login round-trip lands here as /login#error=oauth(_unavailable).
  const [oauthError] = useState(() => {
    try {
      return new URLSearchParams(window.location.hash.replace(/^#/, "")).get("error");
    } catch {
      return null;
    }
  });
  // COG-13: an email-collision reject also carries the provider it tried (#…&provider=github).
  const [oauthProvider] = useState(() => {
    try {
      return new URLSearchParams(window.location.hash.replace(/^#/, "")).get("provider");
    } catch {
      return null;
    }
  });

  useEffect(() => {
    // Scrub the #error so a later Back-nav doesn't replay the notice.
    if (oauthError) window.history.replaceState(null, "", window.location.pathname + window.location.search);
  }, [oauthError]);

  useEffect(() => {
    if (!sessionExpired && !registered) return;
    // (a11y) Move focus to the notice so AT reliably announces WHY the user is back
    // at login — a role="alert" inserted already-populated is announced
    // inconsistently (WCAG 4.1.3). (UX) Scrub `expired` from this history entry so
    // a later Back-nav doesn't replay the notice without a fresh expiry; keep
    // `from` so the post-login redirect still works.
    noticeRef.current?.focus();
    navigate(".", { replace: true, state: navState?.from ? { from: navState.from } : null });
    // Mount-only: snapshot already captured; no deps.

  }, []);

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await signIn(values.email, values.password);
      navigate(from, { replace: true });
    } catch (err) {
      setFormError(
        err instanceof ApiError
          ? err.message
          : "Could not sign in. Please try again.",
      );
    }
  });

  return (
    <AuthLayout>
      <header className={styles.head}>
        <p className="u-eyebrow">Welcome back</p>
        <h2 className={styles.title}>Sign in to your control plane</h2>
      </header>

      {sessionExpired && !formError && (
        <div className={styles.notice} ref={noticeRef} tabIndex={-1}>
          <Callout tone="warning" title="Your session expired">
            You were signed out for security. Sign in again to pick up where you left off.
          </Callout>
        </div>
      )}

      {registered && !formError && (
        <div className={styles.notice} ref={noticeRef} tabIndex={-1}>
          <Callout tone="info" title="Verify your email to finish signing up">
            {registrationEmail ? (
              <>
                We sent a verification link to <strong>{registrationEmail}</strong>. Click it, then
                sign in below.
              </>
            ) : (
              <>We sent you a verification link. Click it, then sign in below.</>
            )}
            {registrationEmail &&
              (resent ? (
                <span className={styles.resentNote}> Sent again — check your inbox (and spam).</span>
              ) : (
                <>
                  {" "}
                  Didn't get it?{" "}
                  <button
                    type="button"
                    className={styles.resend}
                    onClick={() => {
                      // Enumeration-safe: always show "Sent again" on settle, never reveal
                      // whether the account exists. .finally() does NOT handle rejection
                      // (memory/javascript-master-vault-v2/concepts/Promise_catch_Method.md:71-79
                      // — a rejection flows through .finally() unchanged), so a network/5xx
                      // failure would surface as an unhandled rejection. .catch() swallows it.
                      void resendVerification(registrationEmail)
                        .catch(() => {})
                        .finally(() => setResent(true));
                    }}
                  >
                    Resend email
                  </button>
                </>
              ))}
          </Callout>
        </div>
      )}

      {oauthError && !formError && (
        <div className={styles.notice}>
          {oauthError === "suspended" ? (
            <Callout tone="danger" title="Organization suspended">
              This organization has been suspended. Contact support@essentialai.uk to restore access.
            </Callout>
          ) : oauthError === "email_exists" ? (
            <Callout tone="warning" title="Account already exists">
              An account with that email already exists. Sign in below with your email and password
              {oauthProvider
                ? `, then link ${PROVIDER_LABEL[oauthProvider as SocialProvider] ?? "the provider"} from Settings → Linked Accounts.`
                : ", then link the provider from Settings → Linked Accounts."}
            </Callout>
          ) : (
            <Callout tone="danger" title="Sign-in didn't complete">
              We couldn't sign you in with that provider. Try again, or use your email and password.
            </Callout>
          )}
        </div>
      )}

      <form className={styles.form} onSubmit={onSubmit} noValidate>
        {formError && <div className={styles.formError} role="alert">{formError}</div>}

        <TextField
          label="Work email"
          type="email"
          autoComplete="email"
          placeholder="you@company.com"
          required
          error={errors.email?.message}
          {...register("email")}
        />
        <TextField
          label="Password"
          type="password"
          autoComplete="current-password"
          placeholder="••••••••"
          required
          error={errors.password?.message}
          {...register("password")}
        />

        <div className={styles.forgotRow}>
          <Link to="/forgot-password">Forgot your password?</Link>
        </div>

        <Button type="submit" size="lg" fullWidth loading={isSubmitting}>
          Sign in
        </Button>
      </form>

      <SocialButtons verb="Continue" />

      <p className={styles.alt}>
        New to Cogent? <Link to="/register">Create an organization</Link>
      </p>
    </AuthLayout>
  );
}
